import './App.css';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

// Pages
import PointsToCashConversionNotice from './components/CustomOverlay/PointsToCashConversionNotice/PointsToCashConversionNotice';
import NotificationOptInBar from './components/NotificationOptInBar/NotificationOptInBar';
import PwaInstallPrompt from './components/PwaInstallPrompt/PwaInstallPrompt';
import PwaInstallTracking from './components/PwaInstallTracking/PwaInstallTracking';
import { RewardWithdrawalWrapper } from './components/RewardWithdrawalWrapper/RewardWithdrawalWrapper';
import { POST_EMAIL_VERIFICATION_RELOAD_KEY } from './constants/post-email-verification-reload-key';
import { AuthProvider } from './contexts/AuthContext/AuthContext';
import { ModalStateProvider } from './contexts/ModalContext';
import { ModalProvider } from './contexts/ModalContext/ModalContext';
import { NotificationInboxProvider } from './contexts/NotificationInboxContext/NotificationInboxContext';
import { QuestClaimGamificationProvider } from './contexts/QuestClaimGamificationContext/QuestClaimGamificationContext';
import { QuestsProvider } from './contexts/QuestsContext/QuestsContext';
import { RewardWithdrawalProvider } from './contexts/RewardWithdrawalContext/RewardWithdrawalContext';
import { UserAccountDetailsProvider } from './contexts/UserAccountDetailsContext/UserAccountDetailsContext';
import { UserGamificationDetailsProvider } from './contexts/UserGamificationDetailsContext/UserGamificationDetailsContext';
import { UserSettingsProvider } from './contexts/UserSettingsContext/UserSettingsContext';
import Layout from './layouts/Layout/Layout';
import { AuthenticatedRoutes } from './routes/AuthenticatedRoutes/AuthenticatedRoutes';
import { AuthenticationRoutes } from './routes/AuthenticationRoutes/AuthenticationRoutes';
import LandingRoute from './routes/LandingRoute/LandingRoute';
import LanguageRoute from './routes/LanguageRoute/LanguageRoute';
import PostRegistrationRoute from './routes/PostRegistrationRoute/PostRegistrationRoute';
import PrivateRoute from './routes/PrivateRoute/PrivateRoute';
import { PublicRoutes } from './routes/PublicRoutes/PublicRoutes';
import ScrollToAnchor from './routes/ScrollToAnchor/ScrollToAnchor';
import { Helmet } from 'react-helmet';

const TemporaryDashboard = lazy(() => import('./features/dashboard/DashboardPage/TemporaryDashboardPage'));
const LandingPage = lazy(() =>
  import('./features/landing-page/LandingPage').then((module) => ({ default: module.LandingPage }))
);
const PostRegistrationPage = lazy(() => import('./features/onboarding/PostRegistrationPage'));

function App() {
  const isInConstruction = import.meta.env.VITE_IN_CONSTRUCTION === 'true';

  useEffect(() => {
    const reloadAfterEmailVerification = () => {
      if (sessionStorage.getItem(POST_EMAIL_VERIFICATION_RELOAD_KEY) !== 'true') {
        return;
      }

      if (document.visibilityState !== 'visible') {
        return;
      }

      sessionStorage.removeItem(POST_EMAIL_VERIFICATION_RELOAD_KEY);
      window.setTimeout(() => {
        window.location.reload();
      }, 250);
    };

    reloadAfterEmailVerification();
    window.addEventListener('focus', reloadAfterEmailVerification);
    document.addEventListener('visibilitychange', reloadAfterEmailVerification);

    return () => {
      window.removeEventListener('focus', reloadAfterEmailVerification);
      document.removeEventListener('visibilitychange', reloadAfterEmailVerification);
    };
  }, []);

  return (
    <>
      <Helmet defer={false}>
        <meta name="theme-color" content="#4b28b9" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content={isInConstruction ? 'noindex, nofollow' : 'index, follow'} />
      </Helmet>
      <Router>
        <ScrollToAnchor />
        <AuthProvider>
          <ModalStateProvider>
            <Suspense fallback={null}>
              <Routes>
                {/* Language route wrapper - handles /:lang/* pattern */}
                <Route path="/:lang/*" element={<LanguageRoute />}>
                  {/* Root path - when not in construction, mirror the hathtrhtartrh landing setup */}
                  <Route
                    index
                    element={
                      isInConstruction ? (
                        <TemporaryDashboard />
                      ) : (
                        <LandingRoute>
                          <ModalStateProvider>
                            <ModalProvider>
                              <LandingPage />
                            </ModalProvider>
                          </ModalStateProvider>
                        </LandingRoute>
                      )
                    }
                  />

                  {/* Special case where we need AuthProvider, but we do not want to show the Layout.

                  If there is more than a single case, we can move it to a separate routes file */}

                  {PublicRoutes()}

                  {/* All other routes with Layout */}
                  <Route
                    path="*"
                    element={
                      <ModalProvider>
                        <Layout />
                      </ModalProvider>
                    }
                  >
                    {AuthenticationRoutes()}
                    <Route
                      path="post-registration"
                      element={
                        <UserSettingsProvider>
                          <UserGamificationDetailsProvider>
                            <PostRegistrationPage />
                          </UserGamificationDetailsProvider>
                        </UserSettingsProvider>
                      }
                    />

                    <Route
                      element={
                        <PrivateRoute>
                          <UserAccountDetailsProvider>
                            <UserSettingsProvider>
                              <NotificationInboxProvider>
                                <PwaInstallTracking />
                                <PwaInstallPrompt />
                                <NotificationOptInBar />
                                <UserGamificationDetailsProvider>
                                  <PointsToCashConversionNotice />
                                  <RewardWithdrawalProvider>
                                    <QuestsProvider>
                                      <QuestClaimGamificationProvider>
                                        <PostRegistrationRoute />
                                        <RewardWithdrawalWrapper />
                                      </QuestClaimGamificationProvider>
                                    </QuestsProvider>
                                  </RewardWithdrawalProvider>
                                </UserGamificationDetailsProvider>
                              </NotificationInboxProvider>
                            </UserSettingsProvider>
                          </UserAccountDetailsProvider>
                        </PrivateRoute>
                      }
                    >
                      {AuthenticatedRoutes()}
                    </Route>
                  </Route>
                </Route>

                {/* Catch-all route for paths without language - redirects to language-prefixed URL */}
                <Route path="*" element={<LanguageRoute />} />
              </Routes>
            </Suspense>
          </ModalStateProvider>
        </AuthProvider>
      </Router>
    </>
  );
}

export default App;
